import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Copy, Check, ExternalLink, ListChecks, PlusSquare, ClipboardPaste, Download, Sparkles } from "lucide-react";
import { MillieAvatar } from "./MascotBubble";
import { STORE_APPS, openStoreApp, platform } from "../lib/storeApps";

const ICONS = [ListChecks, PlusSquare, ClipboardPaste];

export default function SendToAppSheet({ open, onClose, shop, text, count, onAdvanced }) {
  const [copied, setCopied] = useState(false);
  const cfg = STORE_APPS[shop] || STORE_APPS.woolies;
  const p = platform();

  const go = () => {
    navigator.clipboard?.writeText(text).then(() => { setCopied(true); toast.success(`${count} products copied`); }).catch(() => toast.error("Couldn't copy — use the Copy button below"));
    openStoreApp(shop);
  };
  const copyOnly = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); toast.success("List copied"); } catch { toast.error("Couldn't copy"); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
          className="fixed inset-0 z-50 bg-sorted-ink/40 backdrop-blur-sm flex items-end justify-center" data-testid="send-to-app-sheet">
          <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-[#FFF6F1] rounded-t-[2rem] p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3">
              <MillieAvatar size={44} float={false} mood="point" />
              <div className="flex-1">
                <p className="font-hand text-2xl text-sorted-ink leading-none">Send to the {cfg.label} app</p>
                <p className="text-xs text-sorted-slate font-semibold">I've named every item the way {cfg.label} does, so they come straight up.</p>
              </div>
              <button onClick={onClose} aria-label="Close" data-testid="send-app-close" className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-sorted-slate"><X size={16} strokeWidth={3} /></button>
            </div>

            <button onClick={go} data-testid="send-app-go" className="mt-5 w-full text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-soft text-base" style={{ background: cfg.color }}>
              {copied ? <Check size={18} strokeWidth={3} /> : <Copy size={18} strokeWidth={2.5} />} Copy {count} products &amp; open {cfg.label}
            </button>
            <p className="text-[11px] text-center text-sorted-slate font-semibold mt-2">{p === "desktop" ? "Opens woolworths/coles Lists in a new tab." : `Opens the ${cfg.label} app if it's installed.`}</p>

            <div className="mt-5 space-y-2" data-testid="send-app-steps">
              <p className="text-xs font-bold text-sorted-ink">Then, in the {cfg.label} app:</p>
              {cfg.steps.map(([title, body], i) => {
                const Icon = ICONS[i];
                return (
                  <div key={title} className="bg-white rounded-2xl p-3 flex items-start gap-3 border-2 border-[#FDECEF]" data-testid={`send-app-step-${i + 1}`}>
                    <span className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 text-white" style={{ background: cfg.color }}><Icon size={17} strokeWidth={2.5} /></span>
                    <div>
                      <p className="text-sm font-bold text-sorted-ink leading-tight"><span className="text-sorted-slate mr-1">{i + 1}.</span>{title}</p>
                      <p className="text-[11px] text-sorted-slate font-semibold mt-0.5">{body}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <button onClick={copyOnly} data-testid="send-app-copy" className="bg-white border-2 border-[#FDECEF] text-sorted-ink text-xs font-bold py-2.5 rounded-full flex items-center justify-center gap-1"><Copy size={13} strokeWidth={2.5} /> Copy list only</button>
              <a href={p === "iphone" ? cfg.appstore : cfg.play} target="_blank" rel="noreferrer" data-testid="send-app-get" className="bg-white border-2 border-[#FDECEF] text-sorted-ink text-xs font-bold py-2.5 rounded-full flex items-center justify-center gap-1"><Download size={13} strokeWidth={2.5} /> Get the {cfg.label} app</a>
            </div>

            {onAdvanced && (
              <button onClick={onAdvanced} data-testid="send-app-advanced" className="mt-4 w-full text-[11px] font-bold text-sorted-slate flex items-center justify-center gap-1">
                <Sparkles size={12} /> Want Millie to fill the trolley for you? Set up the 1-tap helper <ExternalLink size={11} />
              </button>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
